import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Briefcase, GraduationCap, Heart, Shield, TrendingUp, Plane, Gavel } from "lucide-react";

// Migration Pathways Array with Route Paths
const migrationPathways = [
  { 
    id: 1, 
    title: "Skilled Worker / Employment", 
    description: "Map your role, confirm eligibility, and file end-to-end.", 
    icon: Briefcase, 
    route: "/services/skilled-worker-employment-visas" 
  },
  { 
    id: 2, 
    title: "Study Permits & Graduate Pathways", 
    description: "Admissions, GTE/finances, and post-study options.", 
    icon: GraduationCap, 
    route: "/services/study-permits-graduate-pathways" 
  },
  { 
    id: 3, 
    title: "Family & Dependent Visas", 
    description: "Spouse/partner, children, and reunification routes.", 
    icon: Heart, 
    route: "/services/family-and-dependent-visas" 
  },
  { 
    id: 4, 
    title: "Permanent Residence & Citizenship", 
    description: "Points systems, PR streams, and naturalisation.", 
    icon: Shield, 
    route: "/services/permanent-residence-and-citizenship" 
  },
  { 
    id: 5, 
    title: "Investor & Entrepreneur", 
    description: "Business plans, source-of-funds, and filings.", 
    icon: TrendingUp, 
    route: "/services/investor-and-entrepreneur-routes" 
  },
  { 
    id: 6, 
    title: "Visitor & Short-Stay", 
    description: "Meetings, conferences, training, tourism.", 
    icon: Plane, 
    route: "/services/visitor-and-short-stay" 
  },
  { 
    id: 7, 
    title: "Appeals & Complex Cases", 
    description: "Refusals/overstays; strategy with licensed counsel.", 
    icon: Gavel, 
    route: "/services/appeals-and-complex-cases" 
  },
];

const ScamTypes = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const visibleItems = 5;

  const scrollLeft = () => {
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const scrollRight = () => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, migrationPathways.length - visibleItems));
  };

  return (
    <section className="py-16 bg-gray-50 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Choose your pathway</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Pick the route that fits your goal-then we tailor the steps, documents, and timeline.
          </p>
        </motion.div>

        <div className="relative flex items-center justify-center w-full">
          <button 
            onClick={scrollLeft} 
            className="absolute left-0 z-10 bg-white shadow-lg text-gray-700 rounded-full p-3 hover:bg-gray-50 transition-colors"
            disabled={currentIndex === 0}
          >
            <ChevronLeft size={20} />
          </button>
          
          <div className="w-full max-w-5xl overflow-hidden">
            <motion.div 
              className="flex space-x-6"
              animate={{ x: `-${currentIndex * (100 / visibleItems)}%` }}
              transition={{ type: "tween", duration: 0.5 }}
            >
              {migrationPathways.map((pathway, index) => {
                const IconComponent = pathway.icon;
                return (
                  <motion.div 
                    key={index} 
                    className="bg-white shadow-lg hover:shadow-xl p-6 rounded-xl w-64 flex flex-col items-center flex-shrink-0 transition-shadow duration-300"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="bg-blue-100 p-4 rounded-full mb-4">
                      <IconComponent className="w-8 h-8 text-black" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-bold text-gray-900 text-lg mb-3 text-center">{pathway.title}</h3>
                    <p className="text-gray-600 text-sm text-center mb-6 flex-grow">{pathway.description}</p>
                    <Link to={pathway.route} className="w-full">
                      <button className="w-full px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-lg transition-colors">
                        Learn More
                      </button>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
          
          <button 
            onClick={scrollRight} 
            className="absolute right-0 z-10 bg-white shadow-lg text-gray-700 rounded-full p-3 hover:bg-gray-50 transition-colors"
            disabled={currentIndex >= migrationPathways.length - visibleItems}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ScamTypes;
